import { FastActionClient } from '@NGPVAN/fastaction-client';
import Backbone from 'lib/backbone';
import cookieConsentConfig from 'app/CookieConsentConfiguration';
import databag_config from 'app/databag_config';
import fastactionConfig from 'app/fastactionConfig';
import telemetry from 'app/views/helpers/telemetry';

const VGS_FALLBACK = 'vgsFallback';
const SAVED_CARD = 'savedCard';

/*
Updated: January 23, 2026

Example responses returned by the @NGPVAN/fastaction-client package.
The client wraps all FastAction API responses in a standardized format with success/error handling.
ActionTag flattens the `response.metadata` and `response.user` fields into the Backbone model.

Successful response (user logged in, and saved card):
{
  "success": true,
  "response": {
    "metadata": {
      "dnt": false,
      "authed": false,
      "fastActionSessionId": "hrCWR5nB32025NH81Sc8GZnACmsW"
    },
    "user": {
      "savedCard": {
        "id": 13982,
        "tokenPool": "DemeterTest",
        "ccLastFour": "1111",
        "ccType": "Visa"
      },
      "vgsFallback": null,
      "profiles": {},
      "profileData": {
        "AddressLine1": "123 Main St",
        "City": "Schenectady",
        "StateProvince": "NY",
        "PostalCode": "12345",
        "EmailAddress": "user@example.com",
        "FirstName": "John",
        "LastName": "Doe",
        "last-modified": "2024-05-22T18:29:16.000Z"
      },
      "id": "hedYX4_uQxiwG33M2qNM2GD0JuCu"
    }
  },
  "httpStatus": 200,
  "rawResponse": "..."
}

Successful response (user logged in, and VGS Fallback):
{
  "success": true,
  "response": {
    "metadata": {
      "dnt": false,
      "authed": false,
      "fastActionSessionId": "hrCWR5nB32025NH81Sc8GZnACmsW"
    },
    "user": {
      "savedCard": null,
      "vgsFallback": {
        "vgsFallbackId": "6487",
        "ccLastFour": "1111",
        "ccType": "Visa",
        "vgsPanAlias": "abc1234567890123456",
        "ccExpirationMonth": "03",
        "ccExpirationYear": "2029"
      },
      "profiles": {},
      "profileData": {
        "AddressLine1": "123 Main St",
        "City": "Schenectady",
        "StateProvince": "NY",
        "PostalCode": "12345",
        "EmailAddress": "user@example.com",
        "FirstName": "John",
        "LastName": "Doe",
        "last-modified": "2024-05-22T18:29:16.000Z"
      },
      "id": "hedYX4_uQxiwG33M2qNM2GD0JuCu"
    }
  },
  "httpStatus": 200,
  "rawResponse": "..."
}

Successful response (user not logged in - no user object, only metadata):
{
  "success": true,
  "response": {
    "metadata": {
      "fastActionSessionId": "hrCWR5nB32025NH81Sc8GZnACmsW",
      "dnt": false
    }
  },
  "httpStatus": 200,
  "rawResponse": "..."
}

Error response (profile not found, network error, etc.):
{
  "success": false,
  "errors": [
    {
      "message": "User profile not found.",
      "code": "USER_PROFILE_NOT_FOUND"
    }
  ],
  "httpStatus": 404,
  "rawResponse": "..."
}

Note: The FastActionClient handles session management internally. It automatically:
- Initializes session ID from localStorage on startup
- Persists session ID to localStorage when a logged-in user is returned
- Includes session ID in all API requests
- Clears session ID when logout() is called
*/

var $ = Backbone.$;
var _ = Backbone._;

// Track in-flight profile operations to ensure they complete before page navigation
var pendingProfileOperations = [];

// Helper to register a pending operation
function trackProfileOperation(promise) {
  if (!promise) {
    return promise;
  }

  pendingProfileOperations.push(promise);

  // Remove from array when complete (success or failure)
  promise.always(function() {
    var index = pendingProfileOperations.indexOf(promise);
    if (index > -1) {
      pendingProfileOperations.splice(index, 1);
    }
  });

  return promise;
}

function logInvalidVgsPanAlias(reason) {
  telemetry.trackEvent({
    name: 'ActionTag FastAction VGS Fallback Pan Alias Invalid',
    properties: { Reason: reason }
  });
}

let fastActionClient = null;

function getClient() {
  if (!fastActionClient) {
    const baseUrl = fastactionConfig.urls.base();
    fastActionClient = new FastActionClient({
      baseUrl: baseUrl,
      timeout: 2000
    });
  }
  return fastActionClient;
}


var FastActionUser = (new (Backbone.Model.extend({
  __name__: 'FastActionUser',
  initialize: function () {
    _.bindAll(this, 'nameFromProfile', 'onSyncError', 'getSavedCardMode', 'getCardDisplayValues', 'getSavedCardPayloadValues', 'getProfileData',
      'fetch', 'clear', 'loginAndFetchProfile', 'fetchProfileWithoutLoggingIn',
      'createProfileAfterSubmission', 'updateProfileAfterSubmission'
    );
  },
  nameFromProfile: function () {
    let profileData = this.get('profileData');
    return [profileData?.FirstName, profileData?.MiddleName, profileData?.LastName].filter(s => !!s).join(' ');
  },
  onSyncError: function (model, response) {
    this.trigger('syncError', model, response);
  },
  getSavedCardMode: function () {
    const savedCard = this.get(SAVED_CARD);
    const vgsFallback = this.get(VGS_FALLBACK);
    if (savedCard !== null && savedCard !== undefined) {
      return SAVED_CARD;
    } else if (vgsFallback !== null && vgsFallback !== undefined) {
      return VGS_FALLBACK;
    }
    return null;
  },
  getCardDisplayValues: function () {
    const mode = this.getSavedCardMode();
    if (mode !== null) {
      const cardOrFallback = this.get(mode);
      return { ccType: cardOrFallback.ccType, ccLastFour: cardOrFallback.ccLastFour };
    }
    return null;
  },
  _parseVgsPanAlias: function (value) {
    if (!value || typeof value !== 'string') {
      return null;
    }
    let decoded;
    try {
      decoded = window.atob(value);
    } catch (e) {
      logInvalidVgsPanAlias('base64_decode_failed');
      return null;
    }
    let parsed;
    try {
      parsed = JSON.parse(decoded);
    } catch (e) {
      logInvalidVgsPanAlias('json_parse_failed');
      return null;
    }
    if (!parsed || typeof parsed !== 'object') {
      logInvalidVgsPanAlias('parsed_not_object');
      return null;
    }
    const keys = Object.keys(parsed);
    if (keys.length === 0 || keys.length > 1 || keys[0] !== 'vgsPanAlias') {
      logInvalidVgsPanAlias('unexpected_json_keys');
      return null;
    }
    const alias = parsed.vgsPanAlias;
    if (typeof alias !== 'string') {
      logInvalidVgsPanAlias('alias_not_string');
      return null;
    }
    if (!alias) {
      logInvalidVgsPanAlias('alias_empty');
      return null;
    }
    return alias;
  },
  getSavedCardPayloadValues: function () {
    const mode = this.getSavedCardMode();
    if (mode === null) {
      return null;
    }
    if (mode === SAVED_CARD) {
      const card = this.get(SAVED_CARD);
      telemetry.trackEvent({
        name: 'ActionTag FastAction Saved Payment Payload',
        properties: {
          PaymentMode: SAVED_CARD,
          HasPayload: true
        }
      });
      return {
        CustomerKey: card.customerKey,
        CcInfoKey: card.ccInfoKey
      };
    }
    // vgsFallback
    const fb = this.get(VGS_FALLBACK);
    const alias = this._parseVgsPanAlias(fb.vgsPanAlias);
    if (!alias) {
      telemetry.trackEvent({
        name: 'ActionTag FastAction Saved Payment Payload',
        properties: {
          PaymentMode: VGS_FALLBACK,
          HasPayload: false
        }
      });
      return null;
    }
    telemetry.trackEvent({
      name: 'ActionTag FastAction Saved Payment Payload',
      properties: {
        PaymentMode: VGS_FALLBACK,
        HasPayload: true
      }
    });
    return {
      Account: alias,
      RedactedAccount: alias,
      ExpirationMonth: fb.ccExpirationMonth,
      RedactedExpirationMonth: fb.ccExpirationMonth,
      ExpirationYear: fb.ccExpirationYear,
      RedactedExpirationYear: fb.ccExpirationYear,
    };
  },
  getVgsFallbackIdIfPanAliasMatches: function (panAlias) {
    const vgsFallback = this.get(VGS_FALLBACK);
    if (!vgsFallback || !vgsFallback.vgsPanAlias) {
      return null;
    }
    return vgsFallback.vgsPanAlias === panAlias ? vgsFallback.vgsFallbackId : null;
  },
  // equivalent of Databag.js get_dict(), Databag.js was killed in EVA-250.
  // Used for prefill and to construct the autoprocessing confirm account modal
  getProfileData: function () {
    let dict = this.get('profileData') || {};

    // it's probably OK to include this in the prefill data but we didn't do so when this model
    // was set from databag
    dict = _.omit(dict, 'last-modified');

    // Databag.js GetDict() did this, I'm not 100% sure it's necessary
    let fullName = this.nameFromProfile();
    if (fullName) {
      dict.FullName = fullName;
    }

    return dict;
  },
  // Helper that mimics Backbone.Model.prototype.fetch behavior but for FastActionClient promises
  // Handles: request event, response unwrapping, parse, set, sync event
  _syncWithClient: function(clientPromise) {
    const self = this;
    const deferred = $.Deferred();

    // Trigger 'request' event
    this.trigger('request', this, deferred.promise(), {});

    clientPromise
      .then((res) => {
        if (!res.success) {
          this.onSyncError(self, res);
          deferred.reject(self, res, 'error');
          return;
        }

        const parsedData = this.parse(res.response);
        this.set(parsedData);

        // Trigger 'sync' event
        this.trigger('sync', this, res, {});

        deferred.resolve(res);
      })
      .catch((error) => {
        this.onSyncError(self, error);
        deferred.reject(self, error, 'error');
      });

    return deferred.promise();
  },
  // Fetch FA profile info using the FastActionClient
  fetch: function () {
    const self = this;
    const deferred = $.Deferred();

    if (!cookieConsentConfig.areFunctionalCookiesAccepted()) {
      deferred.reject('Functional cookies not accepted');
      return deferred.promise();
    }

    const client = getClient();

    // Use helper for Backbone work (request, parse, set, sync)
    this._syncWithClient(client.getProfileFromCurrentSession(this.get('tokenPool'))).then((res) => {
      // Custom logic AFTER sync completes (matches old Backbone pattern)
      const data = res.response;

      if (data.metadata?.dnt) {
        $('.at input[name="updateMyProfile"]').prop('checked', false);
      }

      if (data.user?.id) {
        deferred.resolve(res);
      } else {
        deferred.reject(self, res, 'error', 'Unauthorized');
      }
    }, deferred.reject);

    return deferred.promise();
  },
  clear: function () {
    // Clear the Backbone model state and dispatch a custom "clear" event
    Backbone.Model.prototype.clear.call(this);
    return this.trigger('clear');
  },
  // fetch the FA info associated with the account id
  loginAndFetchProfile: function (fastActionAccountId) {
    const client = getClient();

    return this._syncWithClient(
      client.getProfileByIdWithOptionalLogIn({
        fastActionAccountId: fastActionAccountId,
        logIn: true,
        tokenPool: this.get('tokenPool')
      })
    );
  },
  fetchProfileWithoutLoggingIn: function (fastActionAccountId) {
    const client = getClient();

    return this._syncWithClient(
      client.getProfileByIdWithOptionalLogIn({
        fastActionAccountId: fastActionAccountId,
        logIn: false,
        tokenPool: this.get('tokenPool')
      })
    );
  },
  // Create FastAction profile after form submission (new user)
  // Called from FastAction.preSegue() after successful OA submission
  createProfileAfterSubmission: function (request) {
    const client = getClient();

    return trackProfileOperation(
      this._syncWithClient(client.createProfile(request))
    );
  },
  // Update FastAction profile after form submission (existing user)
  // Called from FastAction.preSegue() after successful OA submission
  updateProfileAfterSubmission: function (request) {
    const client = getClient();

    return trackProfileOperation(
      this._syncWithClient(client.updateProfile(request))
    );
  },
  // overload of native Backbone.js method that is called on fetch. Does some data sanitation on bag data
  // before setting it on the model. Copied over from Databag.js in EVA-250, not 100% sure if we need this
  // we believe it's Oberon related
  parse: function (resp) { // jshint ignore:line
    // flatten
    resp = {
      ...resp.user,
      ...resp.metadata,
      errors: resp.errors
    };

    // Normalize profileData: remove keys with undefined values so we match the old API shape (only populated keys).
    // The new client returns all possible keys with undefined for unpopulated values.
    if (resp.profileData && typeof resp.profileData === 'object') {
      Object.keys(resp.profileData).forEach(function (key) {
        if (resp.profileData[key] === undefined) {
          delete resp.profileData[key];
        }
      });
    }

    // Hack from https://github.com/jashkenas/backbone/issues/1069#issuecomment-17511573
    // to reset the model entirely rather than merging in what's in the server
    // This makes sure we don't get into an inconsistent state if any top-level properties have been
    // unset on the model since the last fetch, e.g. if we were previously logged in (`attributes.errors` set)
    // and now we're logging in (`attributes.errors` should be undefined)
    // tokenPool is excluded because it is set from the form definition metadata (not the FA API),
    // and getProfileFromCurrentSession() omits savedCard/vgsFallback from the response when tokenPool is absent.
    _.keys(this.attributes).forEach(function (key) {
      if (resp[key] === undefined && key !== 'tokenPool') {
        resp[key] = undefined;
      }
    });

    let profileData = resp?.profileData;
    if (!profileData) {
      return resp;
    }

    let dotvaluededupe = {};
    let dotValue = {
      regex: /\.Value$/,
      value: '.Value'
    };

    // Trying to de-dupe multiple values like FirstName and FirstName.Value
    // without throwing out keys that don't have a .Value counterpart.
    _.each(profileData, function (value, key, list) {
      key = databag_config.canonical[key] || key;
      if (!dotValue.regex.test(key)) {
        dotvaluededupe[key] = value;
      } else {
        // See if no key with this and .Value is in the response
        // If this is the only one, we want to ignore it.
        var newKey = key.replace(dotValue.regex, '');
        if (_.isUndefined(list[newKey])) {
          dotvaluededupe[newKey] = value;
        }
      }
    });

    let denylist = databag_config.denylist.slice()
      .concat(['id', 'name']).concat(databag_config.pushPullDenylist);

    resp.profileData = _.omit(dotvaluededupe, denylist);

    return resp;
  },
  setProfileDataWithFormState: function (form_state) {
    var state_copy;

    // Denylist values going into the profileData attribute
    state_copy = _.omit(form_state, databag_config.denylist.slice());

    state_copy = _.reduce(state_copy, function (memo, value, key) {
      if (typeof value !== 'string') {
        value = JSON.stringify(value);
      }
      memo[key] = value;
      return memo;
    }, {});

    // Per [DIGITAL-1249], if AddressLine1 is changed, then always overwrite the
    // value of AddressLine2, even if its new value is blank.
    if (this.get('profileData')?.AddressLine1 !== state_copy.AddressLine1) {
      state_copy.AddressLine2 = form_state.AddressLine2 || '';
    }

    let profileData = {
      ...this.get('profileData'),
      ...state_copy
    };
    // Set silent because we don't want to trigger fill events
    this.set('profileData', profileData, { silent: true });
    return this;
  }
}))());

FastActionUser.getPersistedSessionId = function() {
  return getClient().getSessionIdFromStorage();
};

FastActionUser.clearSessionIdFromStorage = function() {
  return getClient().clearSessionIdFromStorage();
};

FastActionUser.logout = function() {
  var deferred = $.Deferred();

  getClient().logout()
    .then(function(res) {
      if (!res.success) {
        deferred.reject(res);
      } else {
        deferred.resolve(res);
      }
    })
    .catch(function(error) {
      deferred.reject(error);
    });

  return deferred.promise();
};

FastActionUser.openFastActionPopupWindow = function(options) {
  return getClient().openFastActionPopupWindow(options);
};

// Wait for all pending profile operations to complete
// Returns a promise that resolves when all pending operations finish (or immediately if none pending)
// Use this before page navigation to ensure profile updates complete and sessions are persisted
FastActionUser.waitForPendingOperations = function() {
  if (pendingProfileOperations.length === 0) {
    // No pending operations, return immediately resolved promise
    return $.Deferred().resolve().promise();
  }

  // Wait for all pending operations to complete (success or failure)
  return $.when.apply($, pendingProfileOperations);
};

export default FastActionUser;
